// utils/filterOTISData.ts

import {
  matchesOrganization,
  matchesAccount,
  matchesDistributionChannel,
} from "../../../utils/otisCombinationUtils";

/** Labels shown as non-selectable options when no org/account is mapped. */
export const NO_ORGANIZATION_ATTACHED = "No Organization Attached";
export const NO_ACCOUNT_ATTACHED = "No Account Attached";

/** Channel-only rows (Total Organizations upload) have organizations: [] but still belong in the grid. */
const keepChannelOnlyRecord = (
  filteredOrganizations: any[],
  hasOrgFilter: boolean,
  hasAccountFilter: boolean
) =>
  filteredOrganizations.length === 0 && !hasOrgFilter && !hasAccountFilter;

/**
 * Filter OTIS data by display names (Distribution Channel name, Organization name, Account name).
 */
export const FilterOTISData = (
    data: any[],
    selectedRepCode?: string | boolean | null,
    selectedOrganization?: string | boolean | null,
    selectedAccountName?: string | boolean | null,
  ): any[] => {
    return data
      .flatMap((record: any) => {
        if (record.isNew) return record;

        const matchesRepCode = selectedRepCode
          ? record.DISTRIBUTION_CHANNEL_NAME === selectedRepCode
          : true;
        if (!matchesRepCode) return null;

        const filteredOrganizations = record.organizations
          .map((org: any) => {
            const filteredAccounts = org.accounts.filter(
              (account: any) =>
                !selectedAccountName || (account.accountName?.trim?.() || account.accountName) === selectedAccountName
            );

            const orgNameMatch = !selectedOrganization || (org.orgName?.trim?.() || org.orgName) === selectedOrganization;
            if (
              orgNameMatch &&
              (filteredAccounts.length > 0 || !selectedAccountName)
            ) {
              return { ...org, accounts: filteredAccounts };
            }
            return null;
          })
          .filter(Boolean);

        if (keepChannelOnlyRecord(filteredOrganizations, !!selectedOrganization, !!selectedAccountName)) {
          return { ...record, organizations: [] };
        }
        if (filteredOrganizations.length === 0) return null;

        return { ...record, organizations: filteredOrganizations };
      })
      .filter(Boolean);
  };

/**
 * Filter OTIS data by IDs (Distribution Channel ID, Organization ID, Account Number).
 * Used for persistent filter matching when navigating between ISBNs.
 * Any undefined ID is treated as "no filter" for that dimension.
 */
export const FilterOTISDataById = (
  data: any[],
  distributionChannelId?: string | null,
  organizationId?: string | null,
  accountNumber?: string | null,
  organizationName?: string | null,
  accountName?: string | null,
  distributionChannelName?: string | null
): any[] => {
  return data
    .flatMap((record: any) => {
      if (record.isNew) return record;

      const matchesDistribution = distributionChannelId
        ? matchesDistributionChannel(record, {
            DISTRIBUTION_CHANNEL: distributionChannelId,
            DISTRIBUTION_CHANNEL_NAME: distributionChannelName,
          })
        : true;
      if (!matchesDistribution) return null;

      const filteredOrganizations = record.organizations
        .map((org: any) => {
          const filteredAccounts = org.accounts.filter(
            (account: any) =>
              !accountNumber ||
              matchesAccount(account, {
                accountNumber,
                accountName,
              })
          );

          const matchesOrg = organizationId
            ? matchesOrganization(org, {
                organizationId,
                orgName: organizationName,
              })
            : true;
          if (
            matchesOrg &&
            (filteredAccounts.length > 0 || !accountNumber)
          ) {
            return { ...org, accounts: filteredAccounts };
          }
          return null;
        })
        .filter(Boolean);

      if (keepChannelOnlyRecord(filteredOrganizations, !!organizationId, !!accountNumber)) {
        return { ...record, organizations: [] };
      }
      if (filteredOrganizations.length === 0) return null;

      return { ...record, organizations: filteredOrganizations };
    })
    .filter(Boolean);
};
